/* eslint-disable react/no-array-index-key */
import React from 'react';

import {
  Heading,
  Paragraph,
  List,
  ListItem,
  TableRow,
  Link,
  Text,
  Container,
} from '@constellation/core';
import { useContent } from '@interstellar/react-app-content';
import {
  StyledTable,
  StyledTableCol,
} from 'client/components/basicTable/BasicTable.styled';
import { TaggedAccordion } from 'client/components/taggedAccordion/TaggedAccordion';
import testIds from 'client/testIds';

import { LimitedCompanyCriteriaContent } from '../LimitedCompanyCriteria.config';
import BackToTopLink from './BackToTopLink';

function CustomerSection() {
  const { customer, backToTopLabel, jumpToASection } =
    useContent<LimitedCompanyCriteriaContent>();

  const {
    id,
    title,
    idAddressVerification,
    expatriates,
    foreignNationals,
    howToCertifyADocument,
    refugees,
  } = customer;

  return (
    <section
      id={id}
      data-testid={testIds.limitedCompanyCriteria.customerSection}
    >
      <Heading
        size="s5"
        marginBottom="05"
        marginTop="05"
        as="h2"
        dataTestId={testIds.limitedCompanyCriteria.customerHeading}
      >
        {title}
      </Heading>

      <TaggedAccordion
        label={
          <Heading as="h3" size="s3">
            {idAddressVerification.title}
          </Heading>
        }
        id={idAddressVerification.id}
        dataTestId={testIds.limitedCompanyCriteria.idAddressVerification}
        variation="inpage1"
      >
        {idAddressVerification.paragraphs.map((item, idx) => (
          <Paragraph key={`idv-${idx}`}>{item}</Paragraph>
        ))}

        <Heading size="s3" as="h4" id={idAddressVerification.singleIDV.id}>
          {idAddressVerification.singleIDV.title}
        </Heading>
        {idAddressVerification.singleIDV.paragraphs.map((item, idx) => (
          <Paragraph key={`single-idv-${idx}`}>{item}</Paragraph>
        ))}

        <Heading
          size="s2"
          as="h5"
          id={idAddressVerification.singleIDVDocuments.id}
        >
          {idAddressVerification.singleIDVDocuments.title}
        </Heading>
        <List marginBottom="05">
          {idAddressVerification.singleIDVDocuments.listItems.map(
            (item, idx) => (
              <ListItem key={`single-idv-docs-${idx}`}>{item}</ListItem>
            ),
          )}
        </List>
        <Paragraph>
          {idAddressVerification.singleIDVDocuments.paragraph}
        </Paragraph>

        <Heading size="s3" as="h4" id={idAddressVerification.standardIDV.id}>
          {idAddressVerification.standardIDV.title}
        </Heading>
        {idAddressVerification.standardIDV.paragraphs.map((item, idx) => (
          <Paragraph key={`standard-idv-${idx}`}>{item}</Paragraph>
        ))}
        <Paragraph weight="bold">
          {idAddressVerification.standardIDV.boldParagraph}
        </Paragraph>

        <Container padding="none" marginBottom="05">
          <StyledTable
            headings={idAddressVerification.standardIDV.table.headings}
            dataTestId="standard-idv-documents-table"
          >
            {idAddressVerification.standardIDV.table.rows.map((row, rowIdx) => (
              <TableRow key={`idv-table-${rowIdx}`}>
                {row.map((el, colIdx) => (
                  <StyledTableCol
                    key={`idv-table-${rowIdx}-${colIdx}`}
                    data-testid={`idv-table-${rowIdx}-${colIdx}`}
                    heading={
                      idAddressVerification.standardIDV.table.headings[colIdx]
                    }
                  >
                    <Text>{el}</Text>
                  </StyledTableCol>
                ))}
              </TableRow>
            ))}
          </StyledTable>
        </Container>
      </TaggedAccordion>

      <TaggedAccordion
        label={
          <Heading as="h3" size="s3">
            {expatriates.title}
          </Heading>
        }
        id={expatriates.id}
        dataTestId={testIds.limitedCompanyCriteria.expatriates}
        variation="inpage1"
      >
        <Paragraph marginBottom="04">{expatriates.paragraph}</Paragraph>
      </TaggedAccordion>

      <TaggedAccordion
        label={
          <Heading as="h3" size="s3">
            {foreignNationals.title}
          </Heading>
        }
        id={foreignNationals.id}
        dataTestId={testIds.limitedCompanyCriteria.foreignNationals}
        variation="inpage1"
      >
        <Paragraph>{foreignNationals.paragraph1}</Paragraph>
        <List marginBottom="05">
          {foreignNationals.listItems.map((item, idx) => (
            <ListItem key={`foreign-nationals-list-${idx}`}>{item}</ListItem>
          ))}
        </List>
        <Paragraph>{foreignNationals.paragraph2}</Paragraph>
        <Paragraph>{foreignNationals.paragraph3}</Paragraph>
        <Paragraph>
          <Link href={foreignNationals.refugeesLink.url}>
            {foreignNationals.refugeesLink.label}
          </Link>
        </Paragraph>
      </TaggedAccordion>

      <TaggedAccordion
        label={
          <Heading as="h3" size="s3">
            {howToCertifyADocument.title}
          </Heading>
        }
        id={howToCertifyADocument.id}
        dataTestId={testIds.limitedCompanyCriteria.howToCertifyADocument}
        variation="inpage1"
      >
        <Paragraph>{howToCertifyADocument.paragraph1}</Paragraph>
        <Paragraph>{howToCertifyADocument.paragraph2}</Paragraph>
        <Heading as="h4" size="s2">
          {howToCertifyADocument.certifyingDocuments.title}
        </Heading>
        <Paragraph>
          {howToCertifyADocument.certifyingDocuments.paragraph1}
        </Paragraph>
        <List marginBottom="05">
          {howToCertifyADocument.certifyingDocuments.listItems.map(
            (item, idx) => (
              <ListItem key={`certifying-docs-list-${idx}`}>{item}</ListItem>
            ),
          )}
        </List>
        <Paragraph>
          {howToCertifyADocument.certifyingDocuments.paragraph2}
        </Paragraph>
        <Paragraph>
          {howToCertifyADocument.certifyingDocuments.paragraph3}
        </Paragraph>
        <Paragraph>
          {howToCertifyADocument.certifyingDocuments.paragraph4}
        </Paragraph>
        <List>
          {howToCertifyADocument.certifyingDocuments.listItems2.map(
            (item, idx) => (
              <ListItem key={`certifying-docs-list-2-${idx}`}>{item}</ListItem>
            ),
          )}
        </List>
      </TaggedAccordion>

      <TaggedAccordion
        label={
          <Heading as="h3" size="s3">
            {refugees.title}
          </Heading>
        }
        id={refugees.id}
        dataTestId={testIds.limitedCompanyCriteria.refugees}
        variation="inpage1"
      >
        <Paragraph>{refugees.paragraph1}</Paragraph>
        <Paragraph weight="bold">{refugees.wecantaccept.title}</Paragraph>
        <List marginBottom="04">
          {refugees.wecantaccept.listItems.map((item, idx) => (
            <ListItem key={`cant-accept-docs-${idx}`}>{item}</ListItem>
          ))}
        </List>
      </TaggedAccordion>

      <BackToTopLink
        dataTestId={`${id}-back-to-top`}
        jumpToASectionId={jumpToASection.id}
      >
        {backToTopLabel}
      </BackToTopLink>
    </section>
  );
}

export default CustomerSection;
